import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Mail, Lock, User } from 'lucide-react';
import HeaderBar from '@/components/HeaderBar';
import AccountSidebar from '@/components/AccountSidebar';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AccountPage({ token, user, logout }) {
    const navigate = useNavigate();
    
    // Email update state
    const [newEmail, setNewEmail] = useState(user?.email || '');
    const [emailPassword, setEmailPassword] = useState('');
    const [updatingEmail, setUpdatingEmail] = useState(false);
    
    // Password update state
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [updatingPassword, setUpdatingPassword] = useState(false);

    useEffect(() => {
        if (user) {
            setNewEmail(user.email);
        }
    }, [user]);

    const handleUpdateEmail = async (e) => {
        e.preventDefault();
        
        if (!newEmail || !emailPassword) {
            toast.error('Please fill in all fields');
            return;
        }

        if (newEmail === user.email) {
            toast.error('New email is same as current email');
            return;
        }

        setUpdatingEmail(true);

        try {
            await axios.put(
                `${API}/api/auth/update-email`,
                {
                    email: newEmail,
                    password: emailPassword
                },
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            toast.success('Email updated successfully! Please login again with your new email.');
            setEmailPassword('');
            // Redirect to login after email update
            setTimeout(() => {
                logout();
            }, 2000);
        } catch (error) {
            const message = error.response?.data?.detail || 'Failed to update email';
            toast.error(message);
        } finally {
            setUpdatingEmail(false);
        }
    };

    const handleUpdatePassword = async (e) => {
        e.preventDefault();

        if (!currentPassword || !newPassword || !confirmPassword) {
            toast.error('Please fill in all fields');
            return;
        }

        if (newPassword.length < 6) {
            toast.error('Password must be at least 6 characters');
            return;
        }

        if (newPassword !== confirmPassword) {
            toast.error('New passwords do not match');
            return;
        }

        setUpdatingPassword(true);

        try {
            await axios.put(
                `${API}/api/auth/update-password`,
                {
                    current_password: currentPassword,
                    new_password: newPassword
                },
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            toast.success('Password updated successfully!');
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
        } catch (error) {
            const message = error.response?.data?.detail || 'Failed to update password';
            toast.error(message);
        } finally {
            setUpdatingPassword(false);
        }
    };

    if (!user) {
        return <div className="flex items-center justify-center h-screen">Loading...</div>;
    }

    return (
        <div className="settings-page">
            {/* Header Bar */}
            <HeaderBar user={user} logout={logout} />

            {/* Settings Header */}
            <div className="settings-header">
                <div className="settings-header-content settings-header-row">
                    <h1 className="settings-title">Account Settings</h1>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate('/')}
                        className="back-button"
                    >
                        ← Back to dashboard
                    </Button>
                </div>
            </div>

            <div className="settings-container">
                <AccountSidebar token={token} />

                {/* Main Content */}
                <div className="settings-main">
                    <div className="settings-content">
                        {/* Preferences Hero Header */}
                        <div className="subscription-hero">
                            <div className="subscription-hero__icon">
                                <User className="w-5 h-5" />
                            </div>
                            <div className="subscription-hero__text">
                                <h2 className="subscription-hero__title">Preferences</h2>
                                <p className="subscription-hero__subtitle">Manage your email, password, and account details.</p>
                            </div>
                        </div>

                        {/* Update Email Section */}
                        <div className="pref-section">
                            <div className="pref-section__header">
                                <div className="pref-section__icon">
                                    <Mail className="w-4 h-4" />
                                </div>
                                <div className="pref-section__heading">
                                    <h3 className="pref-section__title">Update Email Address</h3>
                                    <p className="pref-section__subtitle">Change the email address associated with your account.</p>
                                </div>
                            </div>

                            <form onSubmit={handleUpdateEmail} className="pref-form">
                                <div className="form-group">
                                    <label className="form-label">Current Email</label>
                                    <input
                                        type="email"
                                        value={user?.email || ''}
                                        disabled
                                        className="form-input form-input--readonly"
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">New Email Address</label>
                                    <input
                                        type="email"
                                        value={newEmail}
                                        onChange={(e) => setNewEmail(e.target.value)}
                                        placeholder="Enter new email"
                                        className="form-input"
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">Confirm Password</label>
                                    <input
                                        type="password"
                                        value={emailPassword}
                                        onChange={(e) => setEmailPassword(e.target.value)}
                                        placeholder="Enter your current password"
                                        className="form-input"
                                        required
                                    />
                                    <p className="form-help">
                                        Please enter your password to confirm this change
                                    </p>
                                </div>

                                <Button
                                    type="submit"
                                    disabled={updatingEmail}
                                    className="pref-submit-btn"
                                >
                                    {updatingEmail ? 'Updating...' : 'Update Email'}
                                </Button>
                            </form>
                        </div>

                        {/* Update Password Section */}
                        <div className="pref-section">
                            <div className="pref-section__header">
                                <div className="pref-section__icon pref-section__icon--violet">
                                    <Lock className="w-4 h-4" />
                                </div>
                                <div className="pref-section__heading">
                                    <h3 className="pref-section__title">Change Password</h3>
                                    <p className="pref-section__subtitle">Update your password to keep your account secure.</p>
                                </div>
                            </div>

                            <form onSubmit={handleUpdatePassword} className="pref-form">
                                <div className="form-group">
                                    <label className="form-label">Current Password</label>
                                    <input
                                        type="password"
                                        value={currentPassword}
                                        onChange={(e) => setCurrentPassword(e.target.value)}
                                        placeholder="Enter current password"
                                        className="form-input"
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">New Password</label>
                                    <input
                                        type="password"
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        placeholder="Enter new password (min 6 characters)"
                                        className="form-input"
                                        required
                                        minLength={6}
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">Confirm New Password</label>
                                    <input
                                        type="password"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        placeholder="Confirm new password"
                                        className="form-input"
                                        required
                                    />
                                </div>

                                <Button
                                    type="submit"
                                    disabled={updatingPassword}
                                    className="pref-submit-btn"
                                >
                                    {updatingPassword ? 'Updating...' : 'Change Password'}
                                </Button>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}